import { Column } from 'react-table';
import { Accessor, createAccessor, ExcelColumn } from '@fluentsms/utils';
import { isExcelTableColumn } from '../ExcelTableColumn';

function resolveColumnLabel<TRow extends Record<string, unknown>>(tableColumn: Column<TRow>): string {
  const { id, Header, accessor } = tableColumn;

  if (isExcelTableColumn(tableColumn) && tableColumn.excel.columnName) {
    return tableColumn.excel.columnName;
  }

  if (typeof Header === 'string') {
    return Header;
  }

  if (id) {
    return id;
  }

  if (typeof accessor === 'string') {
    return accessor;
  }

  throw new Error(`Unable to get column label for column`);
}

function resolveColumnAccessor<TRow extends Record<string, unknown>>(
  tableColumn: Column<TRow>,
  options: {
    selectedRowIds: Record<string, boolean>;
  },
): Accessor<TRow> {
  const { selectedRowIds } = options;

  if (isExcelTableColumn(tableColumn) && tableColumn.excel.getValue) {
    const getValue = tableColumn.excel.getValue;

    return (row: TRow, index: number, allRows: readonly TRow[]) =>
      getValue(row, {
        index,
        selectedRowIds,
        allRows,
      });
  }

  const { accessor } = tableColumn;

  if (typeof accessor === 'string') {
    return createAccessor(accessor);
  }

  if (typeof accessor === 'function') {
    return (row: TRow, index: number, allRows: readonly TRow[]) =>
      accessor(row, index, {
        data: allRows as TRow[],
        depth: 0,
        subRows: [],
      });
  }

  throw new Error(
    `Unable to get accessor for column with ID "${tableColumn.id}". It must have an "accessor" property or have "excel.getValue" option set.`,
  );
}

export function toExcelColumn<TRow extends Record<string, unknown>>(
  tableColumn: Column<TRow>,
  options: {
    selectedRowIds: Record<string, boolean>;
  },
): ExcelColumn<TRow> {
  return {
    label: resolveColumnLabel(tableColumn),
    accessor: resolveColumnAccessor(tableColumn, options),
  };
}
